import { cn } from "../../utils/classnames.js";
import { getTargetBlankA11yHint } from "../../utils/i18n.js";
import { Button } from "../atoms/button.js";
import { DropdownMenu, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "../atoms/dropdown-menu.js";
import { useCurrentBreakpoint } from "../../hooks/breakpoints.js";
import { useLayoutContext } from "../../hooks/layout-context.js";
import { LinkButton } from "../atoms/link-button.js";
import { NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "../atoms/navigation-menu.js";
import { DropdownMenuContent } from "../molecules/dropdown-menu.js";
import { NavigationMenu } from "../molecules/navigation-menu.js";
import { useCallback, useMemo } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { MoreHorizontal } from "lucide-react";
import { useLingui } from "@lingui/react";
import { NAVBAR_TESTID, NAV_DROPDOWN_CONTENT_TESTID, NAV_DROPDOWN_TOGGLE_TESTID } from "@bitcart/qa";
//#region src/components/organisms/website-navigation-menu.tsx
const WebsiteNavigationMenu = ({ className: rowClassName, inert }) => {
	const { i18n, _: $__ } = useLingui();
	const { Link, currentRoute, layoutConfig: { navigation: { navBarDisplayCapacity } }, primaryNavCatalog: { iconLinks, labeledLinks } } = useLayoutContext();
	const currentBreakpoint = useCurrentBreakpoint();
	const maxVisibleRowLinks = useMemo(() => navBarDisplayCapacity ?? {
		md: labeledLinks.length,
		lg: labeledLinks.length,
		xl: labeledLinks.length,
		"2xl": labeledLinks.length,
		"3xl": labeledLinks.length
	}, [labeledLinks.length, navBarDisplayCapacity]);
	const shouldDisplayDropdown = useMemo(() => {
		if (currentBreakpoint === "sm") return false;
		else return labeledLinks.length > maxVisibleRowLinks[currentBreakpoint in maxVisibleRowLinks ? currentBreakpoint : "md"];
	}, [
		currentBreakpoint,
		labeledLinks,
		maxVisibleRowLinks
	]);
	const getRowLinkClass = useCallback((n) => cn({
		"md:inline-flex hidden": n <= maxVisibleRowLinks.md,
		"lg:inline-flex hidden": n > maxVisibleRowLinks.md && n <= maxVisibleRowLinks.lg,
		"xl:inline-flex hidden": n > maxVisibleRowLinks.lg && n <= maxVisibleRowLinks.xl,
		"2xl:inline-flex hidden": n > maxVisibleRowLinks.xl && n <= maxVisibleRowLinks["2xl"],
		"3xl:inline-flex hidden": n > maxVisibleRowLinks["2xl"]
	}), [maxVisibleRowLinks]);
	const getDropdownLinkClass = useCallback((n) => cn({
		hidden: n <= maxVisibleRowLinks.md,
		"md:block lg:hidden": n > maxVisibleRowLinks.md && n <= maxVisibleRowLinks.lg,
		"md:block xl:hidden": n > maxVisibleRowLinks.lg && n <= maxVisibleRowLinks.xl,
		"md:block 2xl:hidden": n > maxVisibleRowLinks.xl && n <= maxVisibleRowLinks["2xl"],
		"md:block 3xl:hidden": n > maxVisibleRowLinks["2xl"]
	}), [maxVisibleRowLinks]);
	return /* @__PURE__ */ jsxs(NavigationMenu, {
		className: cn("gap-4", rowClassName),
		"aria-label": $__({
			id: "TizFpt",
			message: "Navigation menu"
		}),
		inert,
		"data-testid": NAVBAR_TESTID,
		children: [
			/* @__PURE__ */ jsx(NavigationMenuList, { children: labeledLinks.map((link, idx) => {
				const a11yAwareLinkProps = link.isExternal ? {
					href: link.href,
					a11yHint: getTargetBlankA11yHint(i18n)
				} : { href: link.href };
				const isActive = !link.isExternal && link.href === currentRoute.pathnameWithHash;
				return /* @__PURE__ */ jsx(NavigationMenuItem, {
					className: getRowLinkClass(idx + 1),
					"aria-label": link.hint ?? link.label,
					children: /* @__PURE__ */ jsx(NavigationMenuLink, {
						active: isActive,
						render: /* @__PURE__ */ jsx(Link, { ...a11yAwareLinkProps }),
						children: /* @__PURE__ */ jsx("span", { children: currentBreakpoint === "md" ? link.shortLabel ?? link.label : link.label })
					})
				}, link.label + link.href);
			}) }),
			/* @__PURE__ */ jsx(NavigationMenuList, {
				className: "gap-4 lg:gap-4 xl:gap-4",
				children: iconLinks?.map(({ icon: Icon, ...link }) => {
					const isActive = !link.isExternal && link.href === currentRoute.pathnameWithHash;
					return /* @__PURE__ */ jsx(NavigationMenuItem, {
						"aria-label": link.hint,
						children: /* @__PURE__ */ jsx(NavigationMenuLink, {
							active: isActive,
							title: link.hint,
							render: /* @__PURE__ */ jsx(LinkButton, {
								size: "icon",
								variant: "ghost",
								href: link.href
							}),
							children: /* @__PURE__ */ jsx(Icon, {
								className: "size-5",
								"aria-hidden": "true"
							})
						})
					}, link.hint + link.href);
				})
			}),
			/* @__PURE__ */ jsxs(DropdownMenu, { children: [/* @__PURE__ */ jsx(DropdownMenuTrigger, {
				render: /* @__PURE__ */ jsx(Button, {
					size: "icon",
					variant: "ghost"
				}),
				className: cn("relative", { hidden: !shouldDisplayDropdown }),
				"aria-label": $__({
					id: "ZwKvOP",
					message: "Show more links"
				}),
				"data-testid": NAV_DROPDOWN_TOGGLE_TESTID,
				children: /* @__PURE__ */ jsx(MoreHorizontal, {})
			}), /* @__PURE__ */ jsx(DropdownMenuContent, {
				align: "start",
				"aria-label": $__({
					id: "ccaIM9",
					message: "More links"
				}),
				className: cn("w-56", { hidden: !shouldDisplayDropdown }),
				"data-testid": NAV_DROPDOWN_CONTENT_TESTID,
				children: /* @__PURE__ */ jsx(DropdownMenuGroup, {
					render: /* @__PURE__ */ jsx("nav", {}),
					children: labeledLinks.map((link, idx) => {
						const isActive = !link.isExternal && link.href === currentRoute.pathnameWithHash;
						return /* @__PURE__ */ jsx(DropdownMenuItem, {
							nativeButton: false,
							render: /* @__PURE__ */ jsx(LinkButton, {
								variant: isActive ? "accent" : "ghost",
								href: link.href
							}),
							className: cn("w-full justify-start text-left", getDropdownLinkClass(idx + 1), { "bg-accent text-accent-foreground": isActive }),
							"aria-label": link.hint ?? link.label,
							children: link.label
						}, link.label + link.href);
					})
				})
			})] })
		]
	});
};
//#endregion
export { WebsiteNavigationMenu };

//# sourceMappingURL=website-navigation-menu.js.map